import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * Cart Items Component
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    requestAnimationFrame(() => this.#renderConfiguratorDetails());
    this.#setupObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /* ================================
     CONFIGURATION RENDERING
  ================================= */

  #setupObserver() {
    if (this.#observer) return;

    this.#observer = new MutationObserver(() => {
      requestAnimationFrame(() => this.#renderConfiguratorDetails());
    });

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  #renderConfiguratorDetails() {
    const rows = this.querySelectorAll('.cart-items__table-row');

    rows.forEach((row) => {
      const detailsCell = row.querySelector('.cart-items__details');
      if (!detailsCell) return;

      if (detailsCell.querySelector('.cart-config-summary')) return;

      const rawBuild = row.dataset.calibreBuild;
      if (!rawBuild) return;

      let buildData;
      try {
        buildData = JSON.parse(rawBuild);
      } catch {
        return;
      }

      const config = buildData.configuration;
      if (!config || typeof config !== 'object') return;

      /* ---------- helpers ---------- */

      const prettifyLabel = (key) =>
        key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());

      const prettifyValue = (value) => {
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }

        if (typeof value === 'string') {
          return value
            .replace(/^[a-z_]+__/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
        }

        return value;
      };

      const priorityForKey = (key) => {
        if (key.startsWith('case')) return 1;
        if (key.startsWith('bezel') || key.startsWith('dial')) return 2;
        if (key.includes('hand')) return 3;
        if (key.startsWith('chrono') || key.startsWith('subdial')) return 4;
        if (key.startsWith('strap') || key.startsWith('bracelet')) return 5;
        return 6;
      };

      /* ---------- build rows ---------- */

      const rowsHtml = Object.entries(config)
        .filter(([key, value]) => {
          if (key === 'logo') return false;
          if (key === 'metal_logo_finish' && (!config.logo || !config.logo.toLowerCase().includes('metal'))) return false;
          return value !== null && value !== false;
        })
        .sort(([a], [b]) => priorityForKey(a) - priorityForKey(b))
        .map(([key, value]) => {
          return `
            <div class="cart-spec-row" style="display:grid; grid-template-columns:110px 1fr; column-gap:8px; font-size:0.82em; line-height:1.4;">
              <span class="cart-spec-label" style="color:rgba(0,0,0,0.6); font-weight:500;">
                ${prettifyLabel(key)}
              </span>
              <span class="cart-spec-value">
                ${prettifyValue(value)}
              </span>
            </div>
          `;
        })
        .join('');

      if (!rowsHtml) return;

      const container = document.createElement('div');
      container.className = 'cart-config-summary';
      container.innerHTML = `
        <div class="cart-spec-card" style="margin-top:8px;">
          <div class="cart-spec-title" style="font-weight:700; margin-bottom:6px;">
            Specifications
          </div>
          <div class="cart-spec-grid" style="display:grid; row-gap:4px;">
            ${rowsHtml}
          </div>
        </div>
      `;

      detailsCell.appendChild(container);
    });
  }

  /* ================================
     QUANTITY HANDLING
  ================================= */

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({ line, quantity, action: 'change' });

    const row = this.refs.cartItemRows?.[line - 1];
    row?.querySelector('text-component')?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) return;

    const rowsToRemove = [
      row,
      ...this.refs.cartItemRows.filter(
        (r) => r.dataset.parentKey === row.dataset.key
      ),
    ];

    rowsToRemove.forEach((r) => r.remove());
  }

  /* ================================
     CART UPDATE PIPELINE
  ================================= */

  updateQuantity(config) {
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.classList.add('cart-items-disabled');

    const body = JSON.stringify({
      line: config.line,
      quantity: config.quantity,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((r) => r.text())
      .then((text) => {
        const parsed = JSON.parse(text);
        resetShimmer(this);

        if (parsed.errors) return;

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            source: 'cart-items-component',
            sections: parsed.sections,
          })
        );

        morphSection(this.sectionId, parsed.sections[this.sectionId]);
        requestAnimationFrame(() => this.#renderConfiguratorDetails());
      })
      .finally(() => {
        this.classList.remove('cart-items-disabled');
        cartPerformance.measureFromMarker(marker);
      });
  }

  handleDiscountUpdate = () => {
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  };

  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const html = event.detail?.data?.sections?.[this.sectionId];
    if (html) {
      morphSection(this.sectionId, html);
      requestAnimationFrame(() => this.#renderConfiguratorDetails());
    }
  };

  get sectionId() {
    return this.dataset.sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
